import { useEffect, useRef, useState } from 'react';
import type { GameBoard, GamePiece, RawPiece } from '@blind-kings/game-core';
import ErrorBoundary from '../../../Components/ErrorBoundary';
import type { LastAction } from '../../../characters';
import type { TeamPalette } from '../../../player-colors';
import type { BoardFocus } from '../hooks/use-optimistic-game-actions';
import BoardFallback from './BoardFallback';
import { createBoardApp, type BoardApp } from './createBoardApp';
import { BoardView } from './BoardView';
import type { CellMark } from './Highlights';

export type BoardCanvasProps = {
  board: RawPiece[][];
  gameBoard?: GameBoard;
  renderVertically?: boolean;
  selectedPiece?: GamePiece;
  displayPieceValues?: boolean;
  actionablePieces?: GamePiece[];
  invalidPieces?: GamePiece[];
  pendingFlipPieceId?: number;
  skinId?: string;
  interactive?: boolean;
  /** Cap devicePixelRatio for lighter preview canvases (e.g. home hero). */
  maxResolution?: number;
  /** Home/preview mode: lower FPS, low-power GPU, no pointer hit-testing. */
  preview?: boolean;
  /** Pause the Pixi ticker (e.g. while a modal covers the hero). */
  paused?: boolean;
  teamPalettes?: Partial<Record<0 | 1, TeamPalette>>;
  playRequest?: {
    action: LastAction;
    settleBoard: RawPiece[][];
  } | null;
  remoteFocus?: BoardFocus;
  onPlayComplete?: (actionId: string) => void;
  selectPiece?: (piece: GamePiece) => void;
  gamePiecesById?: Record<number, GamePiece>;
  /** Restrict hover/select to these piece ids. null/undefined = all when interactive. */
  hoverablePieceIds?: Set<number> | null;
};

function DevBoardCrash() {
  if (
    import.meta.env.DEV &&
    typeof window !== 'undefined' &&
    new URLSearchParams(window.location.search).get('crashBoard') === '1'
  ) {
    throw new Error('Board crash (dev)');
  }
  return null;
}

function BoardCanvasInner({
  board,
  gameBoard,
  renderVertically = false,
  selectedPiece,
  displayPieceValues = true,
  actionablePieces = [],
  invalidPieces = [],
  pendingFlipPieceId,
  skinId = 'classic',
  interactive = true,
  maxResolution,
  preview = false,
  paused = false,
  teamPalettes,
  playRequest,
  remoteFocus,
  onPlayComplete,
  selectPiece,
  gamePiecesById = {},
  hoverablePieceIds = null,
  onReload,
}: BoardCanvasProps & { onReload: () => void }) {
  const hostRef = useRef<HTMLDivElement>(null);
  const appRef = useRef<BoardApp | null>(null);
  const viewRef = useRef<BoardView | null>(null);
  const playedIdsRef = useRef<Set<string>>(new Set());
  const selectRef = useRef(selectPiece);
  const piecesRef = useRef(gamePiecesById);
  const gameBoardRef = useRef(gameBoard);
  const [viewReady, setViewReady] = useState(false);
  const [crash, setCrash] = useState(false);
  selectRef.current = selectPiece;
  piecesRef.current = gamePiecesById;
  gameBoardRef.current = gameBoard;

  const isPreview = preview || maxResolution !== undefined;
  const effectiveInteractive = interactive && !preview;
  const resolutionCap = maxResolution ?? (preview ? 1 : undefined);
  const renderVerticallyRef = useRef(renderVertically);
  renderVerticallyRef.current = renderVertically;

  useEffect(() => {
    const host = hostRef.current;
    if (!host) return;
    let cancelled = false;
    let ro: ResizeObserver | undefined;
    let resizeTimer: ReturnType<typeof setTimeout> | undefined;

    (async () => {
      let boardApp: BoardApp | undefined;
      let view: BoardView | undefined;
      try {
        boardApp = await createBoardApp(host, {
          maxResolution: resolutionCap,
          antialias: !isPreview,
          lowPower: isPreview,
          maxFPS: isPreview ? 30 : undefined,
          interactive: effectiveInteractive,
        });
        if (cancelled) {
          boardApp.destroy();
          return;
        }
        appRef.current = boardApp;

        view = new BoardView({
          app: boardApp.app,
          displayPieceValues,
          interactive: effectiveInteractive,
          teamPalettes,
          onCellPointer: (cell, pieceId) => {
            const byLocation = gameBoardRef.current?.getPieceByLocation?.(cell.y, cell.x);
            const piece =
              byLocation ??
              (pieceId !== undefined ? piecesRef.current[pieceId] : undefined);
            if (piece) selectRef.current?.(piece);
          },
        });
        view.setSkinId(skinId);
        view.setOrientation(renderVerticallyRef.current);
        await view.init();
        if (cancelled) {
          view.destroy();
          boardApp.destroy();
          return;
        }
        viewRef.current = view;
        setViewReady(true);

        const readyApp = boardApp;
        const readyView = view;
        const applySize = (width: number, height: number) => {
          readyView.setOrientation(renderVerticallyRef.current);
          readyApp.resize(width, height);
          readyView.layoutToSize(width, height);
        };

        ro = new ResizeObserver((entries) => {
          const entry = entries[0];
          if (!entry) return;
          const { width, height } = entry.contentRect;
          if (isPreview) {
            clearTimeout(resizeTimer);
            resizeTimer = setTimeout(() => applySize(width, height), 120);
            return;
          }
          applySize(width, height);
        });
        ro.observe(host);

        const applyHostSize = () => {
          const rect = host.getBoundingClientRect();
          applySize(rect.width, rect.height);
        };
        applyHostSize();
        requestAnimationFrame(applyHostSize);
      } catch (err) {
        view?.destroy();
        boardApp?.destroy();
        appRef.current = null;
        viewRef.current = null;
        if (!cancelled) {
          console.error(err);
          setCrash(true);
        }
      }
    })();

    return () => {
      cancelled = true;
      setViewReady(false);
      clearTimeout(resizeTimer);
      ro?.disconnect();
      viewRef.current?.destroy();
      viewRef.current = null;
      appRef.current?.destroy();
      appRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const app = appRef.current?.app;
    if (!app) return;
    if (paused) app.ticker.stop();
    else app.ticker.start();
  }, [viewReady, paused]);

  useEffect(() => {
    viewRef.current?.setSkinId(skinId);
  }, [skinId]);

  useEffect(() => {
    if (!viewReady) return;
    appRef.current?.setInteractive(effectiveInteractive);
    viewRef.current?.setInteractive(effectiveInteractive);
  }, [viewReady, effectiveInteractive]);

  useEffect(() => {
    viewRef.current?.setHoverablePieceIds(hoverablePieceIds ?? null);
  }, [viewReady, hoverablePieceIds]);

  useEffect(() => {
    viewRef.current?.setDisplayPieceValues(displayPieceValues);
  }, [viewReady, displayPieceValues]);

  useEffect(() => {
    const view = viewRef.current;
    if (!viewReady || !view) return;
    view.setTeamPalettes(teamPalettes);
    // Force piece rebuild with new tints on next setBoard
    if (board?.length) {
      void view.setBoard(board, {
        renderVertically,
        selectedPieceId: selectedPiece?.id,
        pendingFlipPieceId,
        marks: [
          ...actionablePieces.map((p) => ({
            x: p.location.x,
            y: p.location.y,
            kind: 'actionable' as const,
          })),
          ...invalidPieces.map((p) => ({
            x: p.location.x,
            y: p.location.y,
            kind: 'invalid' as const,
          })),
        ],
        force: true,
      });
    }
  }, [viewReady, teamPalettes]);

  useEffect(() => {
    const view = viewRef.current;
    if (!viewReady || !view || !board?.length) return;

    const focusMarks: CellMark[] = [];
    if (remoteFocus && !playRequest) {
      focusMarks.push({
        x: remoteFocus.from.x,
        y: remoteFocus.from.y,
        kind: 'focus',
        role: 'from',
      });
      if (
        remoteFocus.to.x !== remoteFocus.from.x ||
        remoteFocus.to.y !== remoteFocus.from.y
      ) {
        focusMarks.push({
          x: remoteFocus.to.x,
          y: remoteFocus.to.y,
          kind: 'focus',
          role: 'to',
        });
      } else {
        focusMarks[0].role = 'to';
      }
    }

    const marks: CellMark[] = [
      ...focusMarks,
      ...actionablePieces.map((p) => ({
        x: p.location.x,
        y: p.location.y,
        kind: 'actionable' as const,
      })),
      ...invalidPieces.map((p) => ({
        x: p.location.x,
        y: p.location.y,
        kind: 'invalid' as const,
      })),
    ];

    void view.setBoard(board, {
      renderVertically,
      selectedPieceId: selectedPiece?.id,
      pendingFlipPieceId,
      marks,
    });
  }, [
    viewReady,
    board,
    renderVertically,
    selectedPiece?.id,
    pendingFlipPieceId,
    actionablePieces,
    invalidPieces,
    remoteFocus,
    playRequest,
  ]);

  useEffect(() => {
    const view = viewRef.current;
    if (!viewReady || !view || !playRequest) return;
    const actionId = playRequest.action.id;
    if (playedIdsRef.current.has(actionId)) {
      onPlayComplete?.(actionId);
      return;
    }
    playedIdsRef.current.add(actionId);

    void (async () => {
      try {
        await view.playAction(playRequest.action, playRequest.settleBoard);
      } catch (err) {
        console.error(err);
        await view.setBoard(playRequest.settleBoard, {
          renderVertically,
          selectedPieceId: selectedPiece?.id,
          pendingFlipPieceId,
          force: true,
        });
      } finally {
        onPlayComplete?.(actionId);
      }
    })();
  }, [viewReady, playRequest, onPlayComplete]);

  if (crash) {
    return <BoardFallback preview={isPreview} onReload={onReload} />;
  }

  return (
    <div
      ref={hostRef}
      className={`relative h-full w-full overflow-hidden${preview ? ' pointer-events-none' : ''}`}
    />
  );
}

export default function BoardCanvas(props: BoardCanvasProps) {
  const [generation, setGeneration] = useState(0);
  const isPreview = props.preview || props.maxResolution !== undefined;
  const onReload = () => setGeneration((n) => n + 1);

  return (
    <ErrorBoundary
      key={generation}
      fallback={<BoardFallback preview={isPreview} onReload={onReload} />}
    >
      <DevBoardCrash />
      <BoardCanvasInner key={generation} {...props} onReload={onReload} />
    </ErrorBoundary>
  );
}
